import { Box, FlexLayout, H3 } from '@deity/falcon-ui';
import { Checkbox, Modal } from '@deity-io/portal-ui';
import { DropDown, DropDownItem } from 'components/Dropdown';
import { ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import { useEffect, useState } from 'react';
import type { FC } from 'react';
import ReactJson from 'react-json-view';
import { useLocation } from 'react-router-dom';
import { useGetRuntimeLogsLazyQuery } from 'services/graphql/generated/hooks';
import type { GetRuntimeLogsQuery } from 'services/graphql/generated/operations';
import { getParams } from 'utils';
import { LogsFilter } from './LogsFilter';

export const LogsTab: FC = () => {
  const { pathname } = useLocation();
  const params: { [key: string]: string } = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [floatingRow, setFloatingRow] = useState<{
    rowId: unknown;
  }>({
    rowId: undefined
  });
  const [modalState, setModalState] = useState<{ isOpen: boolean; data?: any }>({ isOpen: false });
  const [dataLog, setDataLog] = useState<{
    lastDate: undefined | Date;
    firstDate?: Date;
    data: Array<any>;
  }>({
    lastDate: undefined,
    data: []
  });
  const [logFilter, setLogFilter] = useState<{
    startDate?: Date;
    endDate?: Date;
    allowPoll: boolean;
    poll: boolean;
    pollInterval: number;
  }>({ allowPoll: true, poll: true, pollInterval: 10000 });
  const [getRuntimeLogs, { loading: logsLoading, data: logData, fetchMore, error: logError }] =
    useGetRuntimeLogsLazyQuery();

  const pollIntervals = [
    { interval: 2000, label: '2 seconds' },
    { interval: 5000, label: '5 seconds' },
    { interval: 10000, label: '10 seconds' },
    { interval: 30000, label: '30 seconds' }
  ];

  useEffect(() => {
    /**
     * Initial fetch of {limit} records of the top of the stack
     */
    if (params?.packageId && params?.versionUuid && !logData) {
      getRuntimeLogs({
        variables: {
          limit: 50,
          offset: 0,
          versionUuid: params?.versionUuid
        }
      }).then(logs => {
        if (logs.data) {
          processLog(logs.data);
        }

        return logs;
      });
    }
  }, [pathname, logFilter]);

  useEffect(() => {
    let interval;
    if (logFilter.poll && logFilter.allowPoll) {
      interval = setInterval(
        intervalTime => {
          onFetchMore();
        },
        logFilter.pollInterval,
        logFilter.pollInterval
      );
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [logFilter]);

  const onFetchMore = () => {
    if (params?.packageId && fetchMore) {
      // Get all the records that where added to the log since the last poll
      fetchMore({
        variables: {
          limit: 10000,
          startDate: dataLog.lastDate?.toISOString()
        }
      }).then(logs => {
        if (logs.data) {
          processLog(logs.data);
        }

        return logs;
      });
    }
  };

  const onFetchFilter = (startDate?: Date, endDate?: Date | 'now') => {
    if (params?.packageId && startDate && endDate && endDate !== 'now') {
      // Get all the records that where added to the log since the last poll
      fetchMore({
        variables: {
          limit: 10000,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString()
        }
      }).then(logs => {
        if (logs.data) {
          dataLog.data = [];
          processLog(logs.data as GetRuntimeLogsQuery);
        }

        return logs;
      });
    }
  };

  const processLog = (query: GetRuntimeLogsQuery) => {
    const firstRow = query.getRuntimeLogs.data.find(() => true);
    let lastLogDate: Date | undefined;
    if (firstRow) {
      lastLogDate = new Date(firstRow.time);
    } else {
      // no records fetched, use the known last date
      lastLogDate = dataLog.lastDate;
    }
    const mapped = query.getRuntimeLogs.data.map(item => ({ ...item.data, time: new Date(item.time), tag: item.tag }));
    let firstLogDate: Date;
    if (dataLog.firstDate) {
      firstLogDate = dataLog.firstDate;
    } else if (query.getRuntimeLogs.data[query.getRuntimeLogs.data.length - 1]) {
      firstLogDate = new Date(query.getRuntimeLogs.data[query.getRuntimeLogs.data.length - 1].time); // new Date(dataLog.data.pop().time);
    } else {
      // QuickFix for no records
      firstLogDate = new Date();
    }

    setDataLog({ lastDate: lastLogDate, firstDate: firstLogDate, data: [...mapped, ...dataLog.data] });
  };

  const togglePolling = (update: Partial<typeof logFilter>) => {
    setLogFilter({ ...logFilter, ...update });
  };

  if (logError) {
    // eslint-disable-next-line no-console
    console.error('LogError', logError.message);
  }

  let count = 0;

  return dataLog ? (
    <>
      <FlexLayout p="xs" flexDirection="column" height="full">
        <H3>Logs of package</H3>
        <Box display="block">
          Showing the logs from {dataLog.firstDate?.toLocaleString()} to {dataLog.lastDate?.toLocaleString()}
          {dataLog.firstDate && (
            <LogsFilter
              onFilter={filter => {
                setLogFilter({
                  ...logFilter,
                  ...{
                    allowPoll: filter.endDate === 'now',
                    endDate: filter.endDate !== 'now' ? filter.endDate : undefined,
                    startDate: filter.startDate
                  }
                });
                onFetchFilter(filter.startDate, filter.endDate);
              }}
              defaultStartDate={dataLog.firstDate}
              // defaultEndDate={dataLog.lastDate}
            />
          )}
        </Box>
        {logFilter.allowPoll && (
          <Box>
            <H3>Watch for changes</H3>
            <FlexLayout p="xs" flexDirection="row">
              <Checkbox
                checked={logFilter.poll}
                onChange={(e, value) => {
                  togglePolling({ poll: value });
                }}
              />
              <DropDown
                placeholder="Select"
                defaultValue={logFilter.pollInterval}
                defaultDisplayName={pollIntervals.find(item => item.interval === logFilter.pollInterval)?.label}
                onChange={value => {
                  togglePolling({ pollInterval: value });
                }}
              >
                {pollIntervals.map(item => (
                  <DropDownItem key={item.interval} value={item.interval} displayName={item.label}>
                    {item.label}
                  </DropDownItem>
                ))}
              </DropDown>
            </FlexLayout>
          </Box>
        )}
      </FlexLayout>
      <Box style={{ maxHeight: 'calc(100vh - 470px)', overflow: 'auto' }}>
        <table className="logs">
          <thead>
            <tr>
              <th>time</th>
              <th>flow</th>
              <th>data</th>
            </tr>
          </thead>
          <tbody>
            {dataLog.data.map(item => (
              <tr
                // eslint-disable-next-line no-plusplus
                key={count++}
                onClick={() => {
                  setModalState({ isOpen: true, data: item });
                }}
              >
                <td>
                  {(item.time as Date).toLocaleString(undefined, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                    hour12: false
                  })}
                </td>
                <td>{item.pipelineKey}</td>
                <td style={{ overflow: 'clip', maxWidth: '450px', whiteSpace: 'nowrap', textAlign: 'left' }}>
                  {JSON.stringify(item)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false })}
        // title="Log data"`
        // label="Confirm Label"
        // confirmText="Confirm me"
        // closeText="Close me"
        onConfirm={() => {
          setModalState({ isOpen: false });
          // onConfirm(entityUuid);
        }}
      >
        <FlexLayout>
          {modalState.data && modalState.isOpen && (
            <Box p="lg" style={{ height: '800px', overflow: 'auto' }} width="full">
              <ReactJson src={modalState.data} name={null} theme="ashes" collapseStringsAfterLength={18} />
            </Box>
          )}
        </FlexLayout>
      </Modal>
    </>
  ) : null;
};
